// React
import React, { useMemo } from 'react';

// Components
import Typography from '@material-ui/core/Typography';
import Hidden from '@material-ui/core/Hidden';
import ListItem from '@material-ui/core/ListItem';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';

// utils
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { getPropertType, keyEmptyCheck, getFormattedEID } from 'aurora-utils/utils';
import withStyles from '@material-ui/core/styles/withStyles';
import ADGMImage from '../ADGMImage';

// styles
import styles from './styles';

const ADGMSelectCard = props => {
  const {
    classes,
    isProperty,
    data,
    index,
    handleSelect,
    handleRemove,
    isChecked,
    checkboxView,
    isDisabled,
    disabledInfoText,
    children,
  } = props;
  const locale = localStorage.getItem('lang');

  const dataType = useMemo(() => {
    if (isProperty) {
      return 'property';
    }
    if (data?.isPerson) {
      return 'person';
    }
    if (data?.isCompany) {
      return 'company';
    }
    return 'person';
  }, [data]);

  const ImageToShow = useMemo(() => {
    switch (dataType) {
      case 'company':
        return 'prof_company.svg';
      case 'person':
        return 'defUser.svg';
      default:
        return '';
    }
  }, [data]);
  const dataToShow = useMemo(() => {
    switch (dataType) {
      case 'company':
        return {
          userID: data?.companyId,
          userNameEn: data?.companyNameEn || data?.companyName || data?.nameEn,
          userNameAr: data?.companyNameAr || data?.companyName || data?.nameAr,
          userType: data?.userType,
          userCategory: data?.userCategory,
          profile_pic: data?.profile_pic && data?.profile_pic !== null ? data?.profile_pic : ImageToShow,
          details: {
            license_number: data?.tradeLicenseNumber || data?.licenseNumber,
            company_number: data?.mobileNumber || data?.phoneNo,
            company_email: data?.companyEmail || data?.email,
            ...data?.extraFields,
          },
        };
      case 'person':
        return {
          userID: data?.userId,
          userNameEn: data?.nameEn,
          userNameAr: data?.nameAr,
          userType: data?.userType,
          userCategory: data?.userCategory,
          profile_pic: data?.profile_pic && data?.profile_pic !== null ? data?.profile_pic : ImageToShow,
          details: {
            ...(data?.eid && { emirates_ID: getFormattedEID(data?.eid) }),
            ...(data?.passportNo && { Passport_number: data?.passportNo }),
            nationality: locale === 'en' ? data?.nationalityEn : data?.nationalityAr,
            Email: data?.email,
            Phone_Number: data?.phoneNumber || data?.phoneNo,
            ...data?.extraFields,
          },
        };
      case 'property':
        return {
          unitNumber: data?.unitNumber,
          plotAddress: data?.plotAddress,
          buildingName: locale === 'en' ? data?.buildingNameEn : data?.buildingNameAr,
          unitUsage: locale === 'en' ? data?.unitUsageNameEn : data?.unitUsageNameAr,
          propertyType: data?.propertyType,
          details: {
            municipality: locale === 'en' ? data?.municipalityNameEn : data?.municipalityNameAr,
            district: locale === 'en' ? data?.districtNameEn : data?.districtNameAr,
            community: locale === 'en' ? data?.communityNameEn : data?.communityNameAr,
            plot_number: data?.plotNumber,
            prp_number: data?.propertyRegistrationNumber,
            unt_number: 'UNT' + data?.plotId,
          },
        };
      default:
        return {};
    }
  }, [data, locale]);

  return (
    <ListItem
      className={classNames(classes.listItemContainer, {
        [classes.listItemContainerChecked]: isChecked,
        [classes.listItemContainerDisabled]: isDisabled,
      })}
      button
      onClick={() => (data?.manualAddedParty === true ? {} : handleSelect(data))}
    >
      <div className={classNames(classes.listItemHeader, { [classes.listHeaderPropery]: isProperty })}>
        <div className={classes.listItemTitleWrapper}>
          <div className={classes.listItemTitle}>
            {checkboxView && (
              <Checkbox
                edge='start'
                checked={data?.manualAddedParty === true ? false : isChecked}
                indeterminate={false}
                tabIndex={-1}
                disableRipple
                inputProps={{
                  'aria-labelledby': data?.eid,
                }}
                classes={{
                  root: classes.itemCheckbox,
                  checked: data?.manualAddedParty === true ? '' : classes.itemCheckboxChecked,
                  indeterminate: classes.itemCheckboxIndeterminate,
                }}
              />
            )}

            {isProperty === true ? (
              <>
                <Typography className={classes.titleText}>{dataToShow?.plotAddress}</Typography>
                <Hidden mdUp>
                  <Typography className={classes.chip}>
                    {keyEmptyCheck(getPropertType(dataToShow?.propertyType))}
                  </Typography>
                </Hidden>
              </>
            ) : (
              <>
                <div className={classes.userInfoWrapper}>
                  <div className={classes.imgWrapper}>
                    <ADGMImage name={dataToShow?.profile_pic} className={classes.userIcon} alt='user_picture' />
                  </div>
                  <div className={classes.userInfo}>
                    <Hidden xsDown>
                      <Typography className={classes.userType}>
                        #{index + 1} {keyEmptyCheck(dataToShow?.userType)}
                      </Typography>
                    </Hidden>
                    <Typography className={classes.titleText}>{dataToShow?.userNameEn}</Typography>
                    <Hidden smUp>
                      <Typography className={classes.userType}>
                        #{index + 1} {keyEmptyCheck(dataToShow?.userType)}
                      </Typography>
                    </Hidden>
                    <Hidden xsDown>
                      <Typography className={classes.titleText}>{dataToShow?.userNameAr}</Typography>
                    </Hidden>
                  </div>
                </div>
              </>
            )}
          </div>

          {isProperty === false && (
            <>
              <div className={classes.badgeContainer}>
                <Hidden xsDown>
                  <Typography className={classes.chip}>{keyEmptyCheck(dataToShow?.userCategory)}</Typography>
                </Hidden>
                <div className={classes.deleteBtn}>
                  {data?.manualAddedParty && typeof handleRemove === 'function' && (
                    <Button
                      variant='text'
                      onClick={() => handleRemove(data)}
                      startIcon={<ADGMImage name='delete-pma.svg' alt='delete-dustbin' width='14px' height='14px' />}
                    >
                      {keyEmptyCheck('remove')}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        {isProperty === true && (
          <>
            <Hidden smDown>
              <Typography className={classes.chip}>
                {keyEmptyCheck(getPropertType(dataToShow?.propertyType))}
              </Typography>
            </Hidden>
            {dataToShow?.unitUsage && (
              <div className={classes.unitUsageContainer}>
                <span className={classes.unitUsageIcon}>
                  <svg xmlns='http://www.w3.org/2000/svg' width='4' height='4' viewBox='0 0 4 4' fill='none'>
                    <circle cx='2' cy='2' r='2' fill='#CED1D6' />
                  </svg>
                </span>
                <Typography className={classes.unitUsageText}>{dataToShow?.unitUsage}</Typography>
              </div>
            )}
            <Hidden mdUp>
              <div className={classes.addressContainer}>
                <Typography className={classes.addressLabel}>{keyEmptyCheck('Address')}</Typography>
                <Typography className={classes.addressData}>{Object.values(dataToShow?.details).join(',')}</Typography>
              </div>
            </Hidden>
          </>
        )}
      </div>
      {isProperty === true ? (
        <Hidden smDown>
          <div className={classes.summaryContainer}>
            {Object.keys(dataToShow?.details).length &&
              Object.keys(dataToShow?.details)?.map(detailKey => (
                <div key={detailKey} className={classes.summaryItem}>
                  <Typography className={classes.summaryItemLabel}>{keyEmptyCheck(detailKey)}</Typography>{' '}
                  <Typography className={classes.summaryItemValue}>{dataToShow?.details[detailKey]}</Typography>{' '}
                </div>
              ))}
          </div>
        </Hidden>
      ) : (
        <div className={classes.summaryContainer}>
          {Object.keys(dataToShow?.details).length &&
            Object.keys(dataToShow?.details)?.map((key, summaryIndex) =>
              summaryIndex > 1 ? (
                <Hidden xsDown>
                  <div key={key} className={classes.summaryItem}>
                    <Typography className={classes.summaryPersonLabel}>{keyEmptyCheck(key)}</Typography>{' '}
                    <Typography className={classes.summaryPersonValue}>
                      {!isEmpty(dataToShow?.details[key]) ? dataToShow?.details[key] : '-'}
                    </Typography>{' '}
                  </div>
                </Hidden>
              ) : (
                <div key={key} className={classes.summaryItem}>
                  <Typography className={classes.summaryPersonLabel}>{keyEmptyCheck(key)}</Typography>{' '}
                  <Typography className={classes.summaryPersonValue}>
                    {' '}
                    {!isEmpty(dataToShow?.details[key]) ? dataToShow?.details[key] : '-'}
                  </Typography>{' '}
                </div>
              ),
            )}
        </div>
      )}
      {isDisabled && (
        <div className={classes.disableInfo}>
          <ADGMImage name='Info_grey.svg' alt='disable_info' />
          {keyEmptyCheck(disabledInfoText)}
        </div>
      )}
      {children}
    </ListItem>
  );
};

ADGMSelectCard.propTypes = {
  data: PropTypes.object.isRequired,
  isProperty: PropTypes.bool,
  checkboxView: PropTypes.bool,
  isChecked: PropTypes.bool,
  handleRemove: PropTypes.func,
  isDisabled: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  handleSelect: PropTypes.func.isRequired,
  disabledInfoText: PropTypes.string,
  children: PropTypes.node,
};

ADGMSelectCard.defaultProps = {
  checkboxView: true,
  isDisabled: false,
  isProperty: false,
  isChecked: false,
  handleRemove: null,
  disabledInfoText: '',
  children: null,
};
export default withStyles(styles)(ADGMSelectCard);
